import { Divider, Image, Text } from '@belong/ui';
import { IncludedUtilityListingModel } from 'src/api/models';
import { CYCLE_TO_FREQUENCY_MAP, UTILITY_TO_SRC_MAP } from '../../constants';
import { HOUSE_PAGE_STRINGS } from '../../house-page.strings';
import {
  Utilities,
  UtilityName,
  UtilityPaymentFrequency,
  UtilityWithName,
} from '../../types';

type UtilitiesSectionProps = {
  utilities: Utilities;
};

export function UtilitiesSection({
  utilities,
}: UtilitiesSectionProps): JSX.Element {
  const includedUtilities: UtilityName[] = [];
  const additionalRequiredFeeUtilities: UtilityWithName[] = [];
  const availableForPurchaseUtilities: UtilityWithName[] = [];

  // `Object.entries` widens the key to `string`, and annotating the destructured
  // parameter instead is unsound under `strictFunctionTypes` — so restore the
  // key type on the entries themselves, which is what the object type says.
  (
    Object.entries(utilities) as [UtilityName, IncludedUtilityListingModel][]
  ).forEach(([utilityName, utility]) => {
    const { responsibility, fee, billingHoaType } = utility || {};

    // Two types of provided by HOA, we only consider included in utilities if UtilityBillingHoaType === IncludedInHoa
    const providedByHoa =
      responsibility === 'ProvidedByHoa' && billingHoaType === 'IncludedInHoa';

    if (providedByHoa || responsibility === 'IncludedInRent') {
      includedUtilities.push(utilityName);
    } else if (responsibility === 'AvailableForPurchase' && fee) {
      availableForPurchaseUtilities.push({ utilityName, ...utility });
    } else if (responsibility === 'AdditionalRequiredFee' && fee) {
      additionalRequiredFeeUtilities.push({ utilityName, ...utility });
    }
  });

  const otherUtilities = [
    {
      title: HOUSE_PAGE_STRINGS['utilities.charges'],
      utilities: additionalRequiredFeeUtilities,
    },
    {
      title: HOUSE_PAGE_STRINGS['utilities.purchases'],
      utilities: availableForPurchaseUtilities,
    },
  ].filter((utilitiesGroup) => utilitiesGroup.utilities.length);

  return (
    <>
      {!includedUtilities.length ? (
        <div className="flex justify-between">
          <Text fontWeight="semibold" variant="p1">
            {HOUSE_PAGE_STRINGS['utilities.included']}
          </Text>
          <Text variant="p1">{HOUSE_PAGE_STRINGS['utilities.none']}</Text>
        </div>
      ) : (
        <>
          <div className="mb-sm">
            <Text fontWeight="semibold">
              {HOUSE_PAGE_STRINGS['utilities.included']}
            </Text>
          </div>
          <div className="gap-y-2sm grid grid-cols-4">
            {includedUtilities.map((utilityName) => {
              return (
                <div className="flex flex-col items-center" key={utilityName}>
                  <div className="mb-2xs" style={{ width: 56, height: 56 }}>
                    <Image src={UTILITY_TO_SRC_MAP[utilityName]} alt="" />
                  </div>
                  <Text variant="p1" className="text-center">
                    {getUtilityDisplayName(utilityName)}
                  </Text>
                </div>
              );
            })}
          </div>
        </>
      )}

      {otherUtilities.map((utilitiesGroup) => (
        <>
          <div className="my-md w-full">
            <Divider />
          </div>
          <Text fontWeight="semibold">{utilitiesGroup.title}</Text>
          {utilitiesGroup.utilities.map((utility: UtilityWithName) => (
            <div
              className="mt-2sm flex justify-between"
              key={utility.utilityName}
            >
              <Text>{utility.utilityName}</Text>
              <Text>
                {getBillingCycle(utility.fee || '', utility.billingCycle)}
              </Text>
            </div>
          ))}
        </>
      ))}
    </>
  );
}

function getBillingCycle(
  fee: string,
  cycle: UtilityPaymentFrequency | undefined,
) {
  return cycle ? `$${fee}${CYCLE_TO_FREQUENCY_MAP[cycle]}` : fee;
}

function getUtilityDisplayName(utilityName: UtilityName) {
  if (utilityName === 'PoolServicing') {
    return (
      <span>
        Pool
        <br />
        Servicing
      </span>
    );
  }

  return utilityName;
}
